
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';
import { Settings, Crown, CreditCard, Trash2, LogOut, KeyRound, Eye, EyeOff } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import MainLayout from '@/components/layout/MainLayout';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const AccountPage = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('account');
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState({
    prenom: '',
    nom: '',
    telephone: '',
    adresse: ''
  });

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;

      try {
        const { data, error } = await supabase
          .from('profils_utilisateur')
          .select('*')
          .eq('id', user.id)
          .single();

        if (error && error.code !== 'PGRST116') {
          console.error('Erreur lors de la récupération du profil:', error);
          return;
        }

        if (data) {
          setProfile({
            prenom: data.prenom || '',
            nom: data.nom || '',
            telephone: data.telephone || '',
            adresse: data.adresse || ''
          });
        }
      } catch (error) {
        console.error('Erreur lors de la récupération du profil:', error);
      }
    };

    fetchProfile();
  }, [user]);

  const handleSave = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('profils_utilisateur')
        .upsert({
          id: user.id,
          prenom: profile.prenom,
          nom: profile.nom,
          telephone: profile.telephone,
          adresse: profile.adresse
        });

      if (error) {
        console.error('Erreur lors de la sauvegarde:', error);
        toast({
          title: t('toasts.error'),
          description: t('toasts.profileSaveError'),
          variant: "destructive",
        });
      } else {
        toast({
          title: t('toasts.success'),
          description: t('toasts.profileSaveSuccess'),
        });
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast({
        title: t('toasts.error'),
        description: t('toasts.unexpectedError'),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const [selectedPlan, setSelectedPlan] = useState<'mensuel' | 'annuel'>('mensuel');
  const [promoCode, setPromoCode] = useState('');
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState<any>(null);
  const [stripeSubscription, setStripeSubscription] = useState<{ subscribed: boolean; plan: string | null; subscription_end: string | null } | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [pricingConfig, setPricingConfig] = useState<Record<string, number>>({
    mensuel: 4.99,
    annuel: 54.99
  });

  // Password change state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handleChangePassword = async () => {
    if (!user?.email) return;

    if (passwordForm.newPassword.length < 6) {
      toast({ title: t('toasts.error'), description: t('toasts.passwordTooShort'), variant: "destructive" });
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast({ title: t('toasts.error'), description: t('toasts.passwordsDontMatch'), variant: "destructive" });
      return;
    }

    setPasswordLoading(true);
    try {
      // Verify current password by re-authenticating
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: passwordForm.currentPassword,
      });

      if (signInError) {
        toast({ title: t('toasts.error'), description: t('toasts.currentPasswordIncorrect'), variant: "destructive" });
        setPasswordLoading(false);
        return;
      }

      // Update password
      const { error: updateError } = await supabase.auth.updateUser({
        password: passwordForm.newPassword,
      });

      if (updateError) throw updateError;

      toast({ title: t('toasts.success'), description: t('toasts.passwordChangeSuccess') });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowPasswordForm(false);
    } catch (error) {
      console.error('Erreur changement mot de passe:', error);
      toast({ title: t('toasts.error'), description: t('toasts.passwordChangeError'), variant: "destructive" });
    } finally {
      setPasswordLoading(false);
    }
  };

  // Check Stripe subscription status
  const checkStripeSubscription = async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase.functions.invoke('check-subscription');
      if (error) {
        console.error('Erreur check-subscription:', error);
        return;
      }
      setStripeSubscription(data);
    } catch (error) {
      console.error('Erreur check-subscription:', error);
    }
  };

  useEffect(() => {
    const fetchSubscription = async () => {
      if (!user) return;
      
      try {
        const { data } = await supabase
          .from('abonnements')
          .select('*')
          .eq('user_id', user.id)
          .eq('statut', 'actif')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (data) {
          setCurrentSubscription(data);
        }
      } catch (error) {
        console.error('Erreur lors de la récupération de l\'abonnement:', error);
      }
    };

    const fetchPricing = async () => {
      try {
        const { data, error } = await supabase
          .from('configuration_prix')
          .select('plan, prix')
          .eq('actif', true);

        if (data && !error) {
          const pricing: Record<string, number> = {};
          data.forEach((p: any) => {
            pricing[p.plan] = p.prix;
          });
          setPricingConfig(prev => ({ ...prev, ...pricing }));
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des prix:', error);
      }
    };

    fetchSubscription();
    fetchPricing();
    checkStripeSubscription();

    // Check for checkout redirect
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('checkout') === 'success') {
      toast({
        title: t('toasts.paymentSuccessTitle'),
        description: t('toasts.paymentSuccessDescription'),
      });
      // Clean URL and refresh subscription
      window.history.replaceState({}, '', '/account');
      setTimeout(() => checkStripeSubscription(), 2000);
    } else if (urlParams.get('checkout') === 'cancel') {
      toast({
        title: t('toasts.paymentCancelTitle'),
        description: t('toasts.paymentCancelDescription'),
        variant: "destructive",
      });
      window.history.replaceState({}, '', '/account');
    }
  }, [user]);

  const handleStripeCheckout = async () => {
    if (!user) return;

    setSubscriptionLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { plan: selectedPlan, promoCode: promoCode.trim() || undefined }
      });

      if (error) throw error;
      if (data?.url) {
        window.open(data.url, '_blank');
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Erreur checkout:', error);
      toast({
        title: t('toasts.error'),
        description: t('toasts.checkoutError'),
        variant: "destructive",
      });
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal');
      if (error) throw error;
      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Erreur portail:', error);
      toast({
        title: t('toasts.error'),
        description: t('toasts.portalError'),
        variant: "destructive",
      });
    } finally {
      setPortalLoading(false);
    }
  };

  const getSubscriptionDisplay = () => {
    // Prioritize Stripe status
    if (stripeSubscription?.subscribed && stripeSubscription.plan) {
      const planLabel = stripeSubscription.plan === 'annuel' ? t('subscription.display.yearlyLabel') : t('subscription.display.monthlyLabel');
      const endDate = stripeSubscription.subscription_end 
        ? new Date(stripeSubscription.subscription_end).toLocaleDateString(i18n.language)
        : '';
      return t('subscription.display.premium', { plan: planLabel, date: endDate });
    }
    if (currentSubscription) {
      if (currentSubscription.plan === 'gratuit') {
        return t('subscription.display.free');
      } else {
        const planLabel = currentSubscription.plan === 'annuel' ? t('subscription.display.yearlyLabel') : t('subscription.display.monthlyLabel');
        return t('subscription.display.planPaid', { plan: planLabel, date: new Date(currentSubscription.date_fin).toLocaleDateString(i18n.language) });
      }
    }
    return t('subscription.display.none');
  };

  const isPremium = stripeSubscription?.subscribed === true;

  const handleDeleteAccount = async () => {
    if (!user) return;
    setDeleteLoading(true);
    try {
      const { error } = await supabase.functions.invoke('delete-user-account', {
        body: { userId: user.id }
      });
      if (error) throw error;
      toast({ title: t('toasts.accountDeletedTitle'), description: t('toasts.accountDeletedDescription') });
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Erreur lors de la suppression du compte:', error);
      toast({ title: t('toasts.error'), description: t('toasts.accountDeleteError'), variant: "destructive" });
    } finally {
      setDeleteLoading(false);
    }
  };

  if (!user) {
    return (
      <MainLayout>
        <div className="ml-0 md:ml-64 min-h-screen flex items-center justify-center">
          <p>{t('notConnected')}</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="ml-0 md:ml-64 min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto space-y-8">
            
            {/* En-tête */}
            <div className="text-center">
              <h1 className="text-3xl font-bold text-nautic mb-2">{t('header.title')}</h1>
              <p className="text-gray-600">{t('header.subtitle')}</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              
              {/* Informations du compte */}
              <div className="lg:col-span-2 space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Settings className="h-5 w-5" />
                      {t('personalInfo.title')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="prenom">{t('personalInfo.firstName')}</Label>
                        <Input
                          id="prenom"
                          value={profile.prenom}
                          onChange={(e) => setProfile(prev => ({ ...prev, prenom: e.target.value }))}
                          placeholder={t('personalInfo.firstNamePlaceholder')}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="nom">{t('personalInfo.lastName')}</Label>
                        <Input
                          id="nom"
                          value={profile.nom}
                          onChange={(e) => setProfile(prev => ({ ...prev, nom: e.target.value }))}
                          placeholder={t('personalInfo.lastNamePlaceholder')}
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="email">{t('personalInfo.email')}</Label>
                      <Input
                        id="email"
                        value={user.email || ''}
                        disabled
                        className="bg-gray-100"
                      />
                      <p className="text-sm text-gray-500">{t('personalInfo.emailNote')}</p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="telephone">{t('personalInfo.phone')}</Label>
                      <Input
                        id="telephone"
                        value={profile.telephone}
                        onChange={(e) => setProfile(prev => ({ ...prev, telephone: e.target.value }))}
                        placeholder={t('personalInfo.phonePlaceholder')}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="adresse">{t('personalInfo.address')}</Label>
                      <Input
                        id="adresse"
                        value={profile.adresse}
                        onChange={(e) => setProfile(prev => ({ ...prev, adresse: e.target.value }))}
                        placeholder={t('personalInfo.addressPlaceholder')}
                      />
                    </div>

                    
                    <div className="space-y-2">
                      <Label htmlFor="abonnement">{t('personalInfo.activeSubscription')}</Label>
                      <Input
                        id="abonnement"
                        value={getSubscriptionDisplay()}
                        disabled
                        className="bg-gray-100"
                      />
                    </div>
                    
                    <div className="space-y-3">
                      <Button 
                        onClick={handleSave} 
                        disabled={loading}
                        className="w-full bg-nautic-gold hover:bg-nautic-lightgold text-nautic"
                      >
                        {loading ? t('personalInfo.saving') : t('personalInfo.save')}
                      </Button>

                      <Separator />

                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => setShowPasswordForm(!showPasswordForm)}
                      >
                        <KeyRound className="h-4 w-4 mr-2" />
                        {t('password.change')}
                      </Button>

                      {showPasswordForm && (
                        <div className="space-y-3 p-4 border rounded-lg bg-muted/30">
                          <div className="space-y-2">
                            <Label htmlFor="currentPassword">{t('password.current')}</Label>
                            <div className="relative">
                              <Input
                                id="currentPassword"
                                type={showCurrentPw ? 'text' : 'password'}
                                value={passwordForm.currentPassword}
                                onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                                placeholder={t('password.currentPlaceholder')}
                              />
                              <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" onClick={() => setShowCurrentPw(!showCurrentPw)}>
                                {showCurrentPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                              </button>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="newPassword">{t('password.new')}</Label>
                            <div className="relative">
                              <Input
                                id="newPassword"
                                type={showNewPw ? 'text' : 'password'}
                                value={passwordForm.newPassword}
                                onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                                placeholder={t('password.newPlaceholder')}
                              />
                              <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" onClick={() => setShowNewPw(!showNewPw)}>
                                {showNewPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                              </button>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="confirmPassword">{t('password.confirm')}</Label>
                            <div className="relative">
                              <Input
                                id="confirmPassword"
                                type={showConfirmPw ? 'text' : 'password'}
                                value={passwordForm.confirmPassword}
                                onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                                placeholder={t('password.confirmPlaceholder')}
                              />
                              <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" onClick={() => setShowConfirmPw(!showConfirmPw)}>
                                {showConfirmPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                              </button>
                            </div>
                          </div>
                          <Button
                            onClick={handleChangePassword}
                            disabled={passwordLoading || !passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword}
                            className="w-full"
                          >
                            {passwordLoading ? t('password.validating') : t('password.validate')}
                          </Button>
                        </div>
                      )}
                      
                      <Separator />
                      
                      <Button 
                        onClick={async () => {
                          await signOut();
                          navigate('/');
                        }}
                        variant="outline"
                        className="w-full"
                      >
                        <LogOut className="h-4 w-4 mr-2" />
                        {t('logout')}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Actions rapides */}
              <div className="space-y-6">
                
                {/* Abonnement Premium */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Crown className="h-5 w-5 text-nautic-gold" />
                      {isPremium ? t('subscription.title') : t('subscription.titleUpgrade')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {isPremium ? (
                      <>
                        <div className="bg-green-50 border border-green-200 p-3 rounded-lg text-sm">
                          <p className="font-medium text-green-800">{t('subscription.active')}</p>
                          <p className="text-green-600 mt-1">{getSubscriptionDisplay()}</p>
                        </div>
                        <Button 
                          onClick={handleManageSubscription}
                          disabled={portalLoading}
                          variant="outline"
                          className="w-full"
                        >
                          <Settings className="h-4 w-4 mr-2" />
                          {portalLoading ? t('subscription.loading') : t('subscription.manage')}
                        </Button>
                      </>
                    ) : (
                      <>
                        <div className="space-y-2">
                          <Label>{t('subscription.choosePlan')}</Label>
                          <Select value={selectedPlan} onValueChange={(value: 'mensuel' | 'annuel') => setSelectedPlan(value)}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="mensuel">{t('subscription.monthly', { price: pricingConfig.mensuel?.toFixed(2) })}</SelectItem>
                              <SelectItem value="annuel">{t('subscription.yearly', { price: pricingConfig.annuel?.toFixed(2) })}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Input
                            value={promoCode}
                            onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                            placeholder={t('subscription.promoPlaceholder')}
                            className="uppercase bg-white"
                          />
                          {promoCode && (
                            <p className="text-xs text-muted-foreground">
                              {t('subscription.promoNote')}
                            </p>
                          )}
                        </div>
                        
                        <Button 
                          onClick={handleStripeCheckout}
                          disabled={subscriptionLoading}
                          className="w-full bg-nautic-gold hover:bg-nautic-lightgold text-nautic"
                        >
                          {subscriptionLoading ? (
                            t('subscription.redirecting')
                          ) : (
                            <>
                              <CreditCard className="h-4 w-4 mr-2" />
                              {t('subscription.subscribe', { price: selectedPlan === 'mensuel' ? t('subscription.monthlyPrice', { price: pricingConfig.mensuel?.toFixed(2) }) : t('subscription.yearlyPrice', { price: pricingConfig.annuel?.toFixed(2) }) })}
                            </>
                          )}
                        </Button>
                        
                        <p className="text-xs text-muted-foreground text-center">
                          {t('subscription.securePayment')}
                        </p>
                      </>
                    )}
                  </CardContent>
                </Card>

                {/* Supprimer le compte */}
                <Card className="border-destructive/30">
                  <CardContent className="pt-4">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button 
                          variant="ghost"
                          size="sm"
                          className="w-full text-destructive hover:text-destructive hover:bg-destructive/10"
                          disabled={deleteLoading}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          {deleteLoading ? t('deleteAccount.deleting') : t('deleteAccount.button')}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>{t('deleteAccount.title')}</AlertDialogTitle>
                          <AlertDialogDescription>
                            {t('deleteAccount.description')}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>{t('deleteAccount.cancel')}</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={handleDeleteAccount}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            {t('deleteAccount.confirm')}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default AccountPage;
